import React, { useMemo } from "react";
import { label, number } from "../../../config";
import { splitString } from "../../../utils";
import CreateProjectPopup from "../../Projects/CreateProject/CreateProjectPopup";
import MoveProjectPopup from "../../Queues/MoveProjectPopup/MoveProjectPopup";
import AddWorkspaceOwner from "../AddWorkspaceOwner";
import MoreMenuPopup from "./MoreMenuPopup";
import QueueCardBody from "./QueueCardBody";
import QueueCardHeader from "./QueueCardHeader";
import QueueTeamTable from "./QueueTeamTable";
import { getSortedData } from "./queue.helper";

/**
 * Queue card component for displaying queue information
 * @author Bhavana
 */
const QueueCard = ({ gridRow, index, queueCardHook, isPopupMode = false, onClose }) => {
  const {
    // State
    showMore,
    sortConfigs,
    searchTerms,
    openAddTeamCardIndex,
    setOpenAddTeamCardIndex,
    queueNameEditIndex,
    queueNameEdits,
    openMovePopup,
    setOpenMovePopup,
    queue,
    setQueue,
    openEditOwnerPopup,
    setOpenEditOwnerPopup,
    openMoreMenuPopup,
    setOpenMoreMenuPopup,
    bookmarkStates,
    setHoveredCardIndex,

    // Refs
    addTeamButtonRefs,
    moveIconRefs,
    editOwnerRefs,
    moreMenuRefs,

    // Redux state
    allQueues,
    user,
    assigneeList,

    // Handlers
    handleShowMore,
    handleSaveOwners,
    handleDeleteWorkspace,
    changeProjectQueue,
  } = queueCardHook;

  const { QueueName, Name, owners, memberCount, projectCount, RoleId, details } = gridRow;
  const remainingOwners = owners ? splitString(owners, ",")?.length - 1 : 0;
  const searchTerm = searchTerms[index] || "";
  const sortedDetails = getSortedData(details, index, sortConfigs, searchTerm);
  const totalTaskCount = details?.reduce((total, project) => total + (project?.taskCount || number.ZERO), number.ZERO) || number.ZERO;

  // Initialize refs for this specific card
  if (!addTeamButtonRefs.current[index]) {
    addTeamButtonRefs.current[index] = React.createRef();
  }
  if (!editOwnerRefs.current[index]) {
    editOwnerRefs.current[index] = React.createRef();
  }
  if (!moreMenuRefs.current[index]) {
    moreMenuRefs.current[index] = React.createRef();
  }

  const isEditing = queueNameEditIndex === index;
  const editedName = queueNameEdits[index] ?? QueueName;
  const isQueueBookmarked = bookmarkStates[gridRow?.QueueId];

  // Memoize filtered queues for move popup
  const filteredQueues = useMemo(() => {
    if (openMovePopup?.index !== index) return [];
    const currentQueueId = gridRow?.QueueId;
    return allQueues?.filter((q) => q?.QueueId !== currentQueueId) || [];
  }, [allQueues, gridRow, openMovePopup?.index, index]);

  /**
   * Memoize initial owners for edit owner popup
   * @author Bhavana
   */
  const initialOwners = useMemo(() => {
    if (!owners || !assigneeList) return [];
    const ownerIds = splitString(owners, ",");
    return ownerIds.map((id) => {
      const user = assigneeList.find((u) => u?.value == id);
      return user ? { value: user?.value, label: user?.label } : { value: id, label: id };
    });
  }, [owners, assigneeList]);

  const cardContent = (
    <>
      <QueueCardHeader
        index={index}
        gridRow={gridRow}
        QueueName={QueueName}
        RoleId={RoleId}
        isEditing={isEditing}
        editedName={editedName}
        isQueueBookmarked={isQueueBookmarked}
        queueCardHook={queueCardHook}
      />

      <QueueCardBody
        index={index}
        gridRow={gridRow}
        Name={Name}
        owners={owners}
        remainingOwners={remainingOwners}
        memberCount={memberCount}
        projectCount={projectCount}
        totalTaskCount={totalTaskCount}
        RoleId={RoleId}
        searchTerm={searchTerm}
        editOwnerRef={editOwnerRefs.current[index]}
        queueCardHook={queueCardHook}
      />

      <QueueTeamTable
        index={index}
        details={details}
        sortedDetails={sortedDetails}
        showMore={showMore}
        RoleId={RoleId}
        queueCardHook={queueCardHook}
      />

      {details?.length > number.THREE && (
        <div className='more-text-container'>
          <span className='more-text cursor-pointer' onClick={() => handleShowMore(index)}>
            {showMore?.includes(index) ? label.LESS : label.MORE}
          </span>
        </div>
      )}

      <>
        {openAddTeamCardIndex === index && (
          <CreateProjectPopup
            anchor={addTeamButtonRefs?.current[index]}
            show={true}
            setShow={() => setOpenAddTeamCardIndex(null)}
            queueId={gridRow?.QueueId}
          />
        )}

        {openMovePopup?.index === index && openMovePopup?.teamIndex !== null && (
          <MoveProjectPopup
            anchor={moveIconRefs.current[`${index}-${openMovePopup?.teamIndex}`]}
            show={true}
            setShow={() => {
              setOpenMovePopup({ index: null, teamIndex: null });
              setQueue({});
            }}
            teamName={user?.operationalTeamName}
            queue={queue}
            setQueue={setQueue}
            allQueues={filteredQueues}
            changeProjectQueue={changeProjectQueue}
          />
        )}

        {openEditOwnerPopup === index && (
          <AddWorkspaceOwner
            anchor={editOwnerRefs.current[index]}
            show={true}
            setShow={() => setOpenEditOwnerPopup(null)}
            onSave={(newOwners) => handleSaveOwners(index, newOwners)}
            initialOwners={initialOwners}
          />
        )}

        <MoreMenuPopup
          anchor={moreMenuRefs.current[index]?.current}
          show={openMoreMenuPopup === index}
          onClose={() => setOpenMoreMenuPopup(null)}
          onDeleteWorkspace={() => handleDeleteWorkspace(index)}
        />
      </>
    </>
  );

  // For popup mode, return just the card content
  if (isPopupMode) {
    return <div className='queue-card popup-mode hover-visible'>{cardContent}</div>;
  }

  // For grid mode, return the full structure
  return (
    <div id={index} className='col-lg-4 col-md-6 col-sm-12'>
      <div className='queue-card hover-visible' onMouseEnter={() => setHoveredCardIndex(index)} onMouseLeave={() => setHoveredCardIndex(null)}>
        {cardContent}
      </div>
    </div>
  );
};

export default QueueCard;
